
import { useEffect, useState } from "react";
import { AdminSidebar } from "@/components/admin/AdminSidebar";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";

export default function SystemSettings() {
  const [allowImport, setAllowImport] = useState(false);
  const [allowRegistration, setAllowRegistration] = useState(false);

  const { data: settings, refetch } = useQuery({
    queryKey: ['systemSettings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cat_system_settings')
        .select('*');

      if (error) throw error;
      return data;
    },
    meta: {
      onError: () => {
        toast.error('Erro ao carregar configurações do sistema');
      }
    }
  });

  useEffect(() => {
    if (settings) {
      const importSetting = settings.find(s => s.key === 'allow_import');
      const registrationSetting = settings.find(s => s.key === 'allow_admin_registration');
      
      setAllowImport(importSetting?.value || false);
      setAllowRegistration(registrationSetting?.value || false);
    }
  }, [settings]);

  const updateSetting = async (key: string, value: boolean) => {
    try {
      const { error } = await supabase
        .from('cat_system_settings')
        .update({ value })
        .eq('key', key);

      if (error) throw error;

      await refetch();
      toast.success('Configuração atualizada com sucesso');
    } catch (error) {
      console.error('Erro ao atualizar configuração:', error);
      toast.error('Erro ao atualizar configuração');
    }
  };

  return (
    <div className="flex bg-background">
      <AdminSidebar />
      
      <main className="flex-1 ml-[250px]">
        <div className="p-6">
          <h1 className="text-3xl font-bold mb-6">Configurações do Sistema</h1>

          <div className="max-w-2xl space-y-8">
            {/* Controle de Importação */}
            <div className="border rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4">Importação de Produtos</h2>
              
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="allow-import">Permitir Importação</Label>
                    <p className="text-sm text-muted-foreground">
                      Quando ativado, permite a importação de produtos via planilha.
                      Quando desativado, a funcionalidade de importação fica indisponível.
                    </p>
                  </div>
                  <Switch
                    id="allow-import"
                    checked={allowImport}
                    onCheckedChange={(checked) => {
                      setAllowImport(checked);
                      updateSetting('allow_import', checked);
                    }}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Controle de Cadastros */}
            <div className="border rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4">Controle de Cadastros</h2>
              
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="allow-registration">Permitir Novos Cadastros</Label>
                    <p className="text-sm text-muted-foreground">
                      Quando ativado, permite que novos usuários se cadastrem na área administrativa.
                      Quando desativado, apenas administradores existentes podem acessar o sistema.
                    </p>
                  </div>
                  <Switch
                    id="allow-registration"
                    checked={allowRegistration}
                    onCheckedChange={(checked) => {
                      setAllowRegistration(checked);
                      updateSetting('allow_admin_registration', checked);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
